import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Sparkles,
  Bell,
  Folder,
  MessageSquare,
  Play,
  Pause,
  ChevronLeft,
  ChevronRight,
  X,
  Share2,
  Check,
  Slack,
  Mail,
  Zap,
  Users,
} from "lucide-react";
import { toast } from "sonner";
import PZeroLogo from "@/components/PZeroLogo";

const CARDS = [
  { id: "intro", label: "Intro", duration: 3500 },
  { id: "triage", label: "Morning triage", duration: 8000 },
  { id: "commitments", label: "Chase commitments", duration: 7500 },
  { id: "reminder", label: "Natural-language reminder", duration: 7500 },
  { id: "projects", label: "Hierarchical projects", duration: 6500 },
  { id: "cta", label: "Get started", duration: 7000 },
];

const TOTAL = CARDS.reduce((s, c) => s + c.duration, 0);

// ---------------------------------------------------------------------------
// Typewriter — streams a string letter-by-letter to simulate AI streaming.
// ---------------------------------------------------------------------------
function useTypewriter(text, { speed = 18, startDelay = 300, active = true } = {}) {
  const [out, setOut] = useState("");
  useEffect(() => {
    if (!active) {
      setOut("");
      return;
    }
    let i = 0;
    setOut("");
    const start = setTimeout(function tick() {
      if (i >= text.length) return;
      setOut(text.slice(0, i + 1));
      i += 1;
      setTimeout(tick, speed);
    }, startDelay);
    return () => clearTimeout(start);
  }, [text, speed, startDelay, active]);
  return out;
}

// ---------------------------------------------------------------------------
// Mock chat bubble helpers
// ---------------------------------------------------------------------------
function UserBubble({ children }) {
  return (
    <div className="flex justify-end mb-3">
      <div className="max-w-[80%] rounded-2xl rounded-tr-sm bg-neutral-900 text-white text-sm px-4 py-2.5 leading-relaxed">
        {children}
      </div>
    </div>
  );
}

function AiBubble({ children }) {
  return (
    <div className="flex items-start gap-2.5 mb-3">
      <div className="h-7 w-7 rounded-md bg-gradient-to-br from-neutral-700 to-neutral-900 flex items-center justify-center flex-shrink-0 mt-0.5">
        <Sparkles size={14} className="text-white" />
      </div>
      <div className="max-w-[85%] rounded-2xl rounded-tl-sm bg-neutral-100 text-neutral-900 text-sm px-4 py-2.5 leading-relaxed">
        {children}
      </div>
    </div>
  );
}

function ToolChip({ name }) {
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded bg-neutral-100 text-neutral-700 border border-neutral-200">
      <Zap size={10} /> tool · {name}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Card renderers
// ---------------------------------------------------------------------------
function IntroCard({ active }) {
  const title = useTypewriter("Meet Priority Zero.", { active, speed: 40 });
  return (
    <div className="flex flex-col items-start justify-center h-full max-w-3xl mx-auto px-8">
      <div className="text-[11px] font-mono uppercase tracking-widest text-neutral-500 mb-5">
        Demo · Quick tour
      </div>
      <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-neutral-900 leading-[1.05] min-h-[1.2em]">
        {title}
        <span className="inline-block w-1 h-12 bg-neutral-900 ml-1 -mb-2 animate-pulse" />
      </h1>
      <p className="mt-6 text-lg text-neutral-600 max-w-2xl leading-relaxed">
        The operating system for Chiefs of Staff and Founder&apos;s Office operators who
        run companies alongside founders. AI copilot, hierarchical projects, cross-channel
        triage — all in one calm, focused room.
      </p>
      <div className="mt-8 flex items-center gap-2 text-sm text-neutral-500">
        <div className="h-6 w-6 rounded-full bg-gradient-to-br from-rose-400 to-orange-400 flex items-center justify-center text-[10px] font-bold text-white">SK</div>
        <span>Sarah Kim · Chief of Staff · Halcyon Labs · Week 4</span>
      </div>
    </div>
  );
}

function TriageCard({ active }) {
  const answer = useTypewriter(
    "Three things worth your attention today —",
    { active, speed: 20, startDelay: 1000 }
  );
  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 h-full max-w-6xl mx-auto px-8 items-center">
      <div className="lg:col-span-2">
        <div className="text-[11px] font-mono uppercase tracking-widest text-neutral-500 mb-3">01 · Morning triage</div>
        <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-neutral-900 leading-tight">
          Wake up. Ask one question.
          <br />
          <span className="text-red-600">Know what matters.</span>
        </h2>
        <p className="mt-4 text-sm text-neutral-600 leading-relaxed">
          The AI ingests every Slack, Gmail, WhatsApp, and calendar signal overnight, then
          filters it down to the 3 things worth your first hour.
        </p>
        <div className="mt-5 flex flex-wrap gap-2">
          <span className="inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-md bg-neutral-100 text-neutral-700"><Slack size={12} /> Slack</span>
          <span className="inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-md bg-neutral-100 text-neutral-700"><Mail size={12} /> Gmail</span>
          <span className="inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-md bg-neutral-100 text-neutral-700"><MessageSquare size={12} /> WhatsApp</span>
        </div>
      </div>
      <div className="lg:col-span-3">
        <div className="rounded-xl border border-neutral-200 bg-white shadow-lg overflow-hidden">
          <div className="px-4 py-2 border-b border-neutral-200 flex items-center gap-2 text-xs text-neutral-500">
            <Sparkles size={12} /> Ask anything · new thread
          </div>
          <div className="p-4">
            <UserBubble>What needs my attention today?</UserBubble>
            <div className="flex items-center gap-2 mb-3 text-[11px] text-neutral-500">
              <ToolChip name="find_events" />
              <ToolChip name="find_commitments" />
              <span className="inline-flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" /> running</span>
            </div>
            <AiBubble>
              <div className="font-medium mb-2">{answer}</div>
              {answer.length > 30 && (
                <ol className="space-y-2 text-sm">
                  <motion.li initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
                    <span className="inline-block px-1.5 py-0.5 rounded text-[10px] bg-red-50 text-red-700 mr-2">URGENCY 5/5</span>
                    <span className="font-medium">Priya (Acme) needs staging env by Friday</span> — she&apos;s demoing to their board Monday.
                  </motion.li>
                  <motion.li initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.2 }}>
                    <span className="inline-block px-1.5 py-0.5 rounded text-[10px] bg-orange-50 text-orange-700 mr-2">URGENCY 4/5</span>
                    <span className="font-medium">David flagged QA slippage</span> — Observability v3 release at risk.
                  </motion.li>
                  <motion.li initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 2.2 }}>
                    <span className="inline-block px-1.5 py-0.5 rounded text-[10px] bg-amber-50 text-amber-700 mr-2">STALE</span>
                    <span className="font-medium">Aisha (Northwind) silent 15 days</span> — renewal signal dropped.
                  </motion.li>
                </ol>
              )}
            </AiBubble>
          </div>
        </div>
      </div>
    </div>
  );
}

function CommitmentsCard({ active }) {
  const answer = useTypewriter("12 open commitments. Here are the 4 that matter most:", {
    active,
    speed: 18,
    startDelay: 800,
  });
  const rows = [
    { who: "Priya Sharma", role: "Acme · Head of Product", what: "Send SOW addendum", due: "today", risk: "at_risk" },
    { who: "Marcus Chen", role: "Beacon Labs · CTO", what: "Confirm postmortem draft", due: "in 2d", risk: "at_risk" },
    { who: "Ken Sato", role: "Halcyon · CFO", what: "Q1 numbers locked for Elena", due: "in 1d", risk: "open" },
    { who: "Aisha Patel", role: "Northwind · VP Growth", what: "Renewal response — 15d silent", due: "overdue", risk: "overdue" },
  ];
  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 h-full max-w-6xl mx-auto px-8 items-center">
      <div className="lg:col-span-2">
        <div className="text-[11px] font-mono uppercase tracking-widest text-neutral-500 mb-3">02 · Chase commitments</div>
        <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-neutral-900 leading-tight">
          Did they do what they
          <br />
          <span className="text-red-600">said they&apos;d do?</span>
        </h2>
        <p className="mt-4 text-sm text-neutral-600 leading-relaxed">
          Every promise made in Slack, email, or a meeting is captured and tracked. Ask in
          plain English — the AI knows who&apos;s owing whom, since when.
        </p>
        <div className="mt-5 text-[11px] font-mono text-neutral-500">tool call: find_commitments(status=&quot;open,at_risk&quot;)</div>
      </div>
      <div className="lg:col-span-3">
        <div className="rounded-xl border border-neutral-200 bg-white shadow-lg overflow-hidden">
          <div className="px-4 py-2 border-b border-neutral-200 flex items-center gap-2 text-xs text-neutral-500">
            <Sparkles size={12} /> Ask anything
          </div>
          <div className="p-4">
            <UserBubble>Who&apos;s owing us stuff right now?</UserBubble>
            <AiBubble>
              <div className="mb-3">{answer}</div>
              {answer.length > 20 && (
                <div className="space-y-2">
                  {rows.map((r, i) => (
                    <motion.div
                      key={r.who}
                      initial={{ opacity: 0, x: -8 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.3 + i * 0.35 }}
                      className="flex items-center gap-3 rounded-lg border border-neutral-200 bg-white px-3 py-2"
                    >
                      <div className="h-8 w-8 rounded-full bg-gradient-to-br from-neutral-300 to-neutral-500 text-white text-[11px] font-bold flex items-center justify-center flex-shrink-0">
                        {r.who.split(" ").map((n) => n[0]).join("").slice(0, 2)}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-neutral-900 truncate">{r.what}</div>
                        <div className="text-[11px] text-neutral-500 truncate">{r.who} · {r.role}</div>
                      </div>
                      <div className={`text-[11px] font-mono px-2 py-0.5 rounded-md whitespace-nowrap ${
                        r.risk === "overdue" ? "bg-red-50 text-red-700"
                          : r.risk === "at_risk" ? "bg-amber-50 text-amber-700"
                          : "bg-neutral-100 text-neutral-700"
                      }`}>{r.due}</div>
                    </motion.div>
                  ))}
                </div>
              )}
            </AiBubble>
          </div>
        </div>
      </div>
    </div>
  );
}

function ReminderCard({ active }) {
  const answer = useTypewriter("Done! ⏱️ You'll be pinged in 3 hours.", {
    active,
    speed: 30,
    startDelay: 1400,
  });
  const [toastShown, setToastShown] = useState(false);
  useEffect(() => {
    if (!active) {
      setToastShown(false);
      return;
    }
    const t = setTimeout(() => {
      if (!toastShown) {
        toast.success("Reminder set · Ping Legal for DPA status", {
          description: "If not kicked off, escalate to Wei.",
          duration: 4000,
        });
        setToastShown(true);
      }
    }, 3200);
    return () => clearTimeout(t);
  }, [active, toastShown]);
  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 h-full max-w-6xl mx-auto px-8 items-center">
      <div className="lg:col-span-2">
        <div className="text-[11px] font-mono uppercase tracking-widest text-neutral-500 mb-3">03 · Natural-language reminders</div>
        <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-neutral-900 leading-tight">
          Type like you think.
          <br />
          <span className="text-red-600">The AI does the wiring.</span>
        </h2>
        <p className="mt-4 text-sm text-neutral-600 leading-relaxed">
          &quot;Remind me to chase Legal in 3 hours.&quot; — no calendar, no form. The AI parses
          the relative time, files it, and fires a browser notification at the exact moment.
        </p>
        <div className="mt-5 inline-flex items-center gap-2 text-[11px] font-mono text-neutral-500">
          <Bell size={12} /> tool: create_reminder(remind_in_hours=3)
        </div>
      </div>
      <div className="lg:col-span-3">
        <div className="rounded-xl border border-neutral-200 bg-white shadow-lg overflow-hidden">
          <div className="px-4 py-2 border-b border-neutral-200 flex items-center gap-2 text-xs text-neutral-500">
            <Sparkles size={12} /> Ask anything
          </div>
          <div className="p-4">
            <UserBubble>remind me to chase Legal for DPA in 3 hours</UserBubble>
            <div className="flex items-center gap-2 mb-3 text-[11px] text-neutral-500">
              <ToolChip name="create_reminder" />
              <span className="inline-flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" /> writing</span>
            </div>
            <AiBubble>
              <div>{answer}</div>
            </AiBubble>
            {answer && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="mt-3 flex items-center gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2"
              >
                <div className="h-7 w-7 rounded-md bg-emerald-500 text-white flex items-center justify-center">
                  <Bell size={14} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-emerald-900">Ping Legal for DPA status</div>
                  <div className="text-[11px] text-emerald-700">If not kicked off, escalate to Wei · fires in 3h 0m</div>
                </div>
                <div className="text-[11px] font-mono text-emerald-700">✓ SAVED</div>
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function ProjectsCard() {
  const verticals = [
    {
      name: "Client Work",
      color: "#EF4444",
      groups: [
        { name: "Active Clients", items: [{ name: "Acme Q1 Platform Rollout", health: 78, status: "ok" }] },
        { name: "At Risk", items: [{ name: "Beacon Labs Data Migration", health: 32, status: "risk" }] },
        { name: "New Deals", items: [{ name: "Ecliptic Q2 Onboarding", health: 65, status: "ok" }] },
      ],
    },
    {
      name: "Internal Ops",
      color: "#2563EB",
      groups: [
        { name: "Infrastructure", items: [{ name: "Zero-trust access rollout", health: 55, status: "ok" }] },
      ],
    },
    {
      name: "HR & People",
      color: "#8B5CF6",
      groups: [
        { name: "Hiring", items: [{ name: "VP Sales hiring loop", health: 65, status: "ok" }] },
      ],
    },
  ];
  const kanban = [
    { col: "To do", tint: "bg-neutral-100 text-neutral-700", n: 6 },
    { col: "In progress", tint: "bg-blue-100 text-blue-700", n: 3 },
    { col: "Blocked", tint: "bg-red-100 text-red-700", n: 2 },
    { col: "Done", tint: "bg-emerald-100 text-emerald-700", n: 4 },
  ];
  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 h-full max-w-6xl mx-auto px-8 items-center">
      <div className="lg:col-span-2">
        <div className="text-[11px] font-mono uppercase tracking-widest text-neutral-500 mb-3">04 · Hierarchical projects</div>
        <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-neutral-900 leading-tight">
          Vertical → Category
          <br />
          <span className="text-red-600">→ Project.</span>
        </h2>
        <p className="mt-4 text-sm text-neutral-600 leading-relaxed">
          Group work the way you actually think about it. Tree view for the map,
          Kanban for the drag. Every project has a health score, an owner and a next action.
        </p>
        <div className="mt-5 flex items-center gap-3 text-xs text-neutral-500">
          <span className="inline-flex items-center gap-1.5"><Folder size={14} /> 7 verticals</span>
          <span className="inline-flex items-center gap-1.5"><Users size={14} /> 20 people</span>
          <span className="inline-flex items-center gap-1.5"><Check size={14} /> 18 projects · 48 tasks</span>
        </div>
      </div>
      <div className="lg:col-span-3 grid grid-cols-2 gap-4">
        {/* Tree */}
        <div className="rounded-xl border border-neutral-200 bg-white shadow-lg overflow-hidden">
          <div className="px-3 py-2 border-b border-neutral-200 text-[10px] font-mono uppercase text-neutral-500">Tree</div>
          <div className="p-3 space-y-3">
            {verticals.map((v) => (
              <div key={v.name}>
                <div className="flex items-center gap-1.5 text-xs font-semibold text-neutral-800">
                  <Folder size={12} style={{ color: v.color }} />
                  {v.name}
                </div>
                <div className="ml-4 mt-1 space-y-1.5">
                  {v.groups.map((g) => (
                    <div key={g.name}>
                      <div className="text-[10px] font-mono uppercase text-neutral-400 mb-0.5">{g.name}</div>
                      {g.items.map((it) => (
                        <div key={it.name} className="flex items-center gap-2 text-xs text-neutral-700 py-0.5">
                          <span className={`h-1.5 w-1.5 rounded-full ${it.status === "risk" ? "bg-red-500" : "bg-emerald-500"}`} />
                          <span className="truncate">{it.name}</span>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
        {/* Kanban */}
        <div className="rounded-xl border border-neutral-200 bg-white shadow-lg overflow-hidden">
          <div className="px-3 py-2 border-b border-neutral-200 text-[10px] font-mono uppercase text-neutral-500">Kanban · Acme Q1</div>
          <div className="p-3 grid grid-cols-2 gap-2">
            {kanban.map((k, i) => (
              <motion.div
                key={k.col}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 + i * 0.12 }}
                className="rounded-md border border-neutral-100 p-2"
              >
                <div className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded ${k.tint} inline-block mb-1.5`}>{k.col}</div>
                <div className="text-lg font-bold text-neutral-900">{k.n}</div>
                <div className="text-[10px] text-neutral-400">tasks</div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function CtaCard({ onExit }) {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const share = () => {
    const url = window.location.origin + "/tour";
    navigator.clipboard?.writeText(url);
    setCopied(true);
    toast.success("Tour link copied", { description: url });
    setTimeout(() => setCopied(false), 2500);
  };
  const googleLogin = () => {
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <div className="flex flex-col items-center justify-center h-full max-w-3xl mx-auto px-8 text-center">
      <div className="text-[11px] font-mono uppercase tracking-widest text-neutral-500 mb-4">05 · Get started</div>
      <h2 className="text-5xl sm:text-6xl font-bold tracking-tight text-neutral-900 leading-[1.05]">
        Ready to run your
        <br />
        founder&apos;s office <span className="text-red-600">like this?</span>
      </h2>
      <p className="mt-6 text-base text-neutral-600 max-w-xl">
        Sign in and explore the fully-seeded Halcyon Labs workspace — 20 people, 18 projects,
        12 pre-populated AI chats. Or share this tour with a founder who needs one of you.
      </p>
      <div className="mt-10 flex flex-col sm:flex-row gap-3 items-center">
        <button
          onClick={googleLogin}
          data-testid="tour-cta-google"
          className="inline-flex items-center gap-2 text-sm px-5 py-3 rounded-md bg-neutral-900 text-white font-medium hover:bg-neutral-800 transition-colors"
        >
          <svg width="16" height="16" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A10.96 10.96 0 0 0 1 12c0 1.77.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
          Continue with Google
        </button>
        <button
          onClick={() => navigate("/login")}
          data-testid="tour-cta-email"
          className="inline-flex items-center gap-2 text-sm px-5 py-3 rounded-md border border-neutral-200 text-neutral-900 font-medium hover:bg-neutral-50 transition-colors"
        >
          Sign in with email
        </button>
        <button
          onClick={share}
          data-testid="tour-cta-share"
          className="inline-flex items-center gap-2 text-sm px-4 py-3 rounded-md text-neutral-600 font-medium hover:text-neutral-900 transition-colors"
        >
          {copied ? <Check size={14} /> : <Share2 size={14} />}
          {copied ? "Copied" : "Share tour"}
        </button>
      </div>
      <div className="mt-8 rounded-md border border-neutral-200 bg-neutral-50 text-[11px] font-mono text-neutral-600 px-3 py-2">
        try instantly · admin@fos.dev · FounderOS!2026
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main tour component
// ---------------------------------------------------------------------------
export default function DemoTour() {
  const navigate = useNavigate();
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const [progress, setProgress] = useState(0); // 0..1 within current card
  const rafRef = useRef(null);
  const startRef = useRef(0);
  const elapsedRef = useRef(0);

  const active = CARDS[idx];

  const goto = useCallback((next) => {
    if (next < 0) next = 0;
    if (next >= CARDS.length) next = CARDS.length - 1;
    setIdx(next);
    setProgress(0);
    startRef.current = performance.now();
    elapsedRef.current = 0;
  }, []);

  const next = useCallback(() => goto(idx + 1), [idx, goto]);
  const prev = useCallback(() => goto(idx - 1), [idx, goto]);

  // Progress tick
  useEffect(() => {
    startRef.current = performance.now();
    elapsedRef.current = 0;
    setProgress(0);
    const tick = (t) => {
      if (paused) {
        startRef.current = t - elapsedRef.current;
      } else {
        const el = t - startRef.current;
        elapsedRef.current = el;
        const p = Math.min(1, el / active.duration);
        setProgress(p);
        if (p >= 1) {
          if (idx < CARDS.length - 1) {
            setIdx((i) => i + 1);
            startRef.current = t;
            elapsedRef.current = 0;
            setProgress(0);
          } else {
            setPaused(true);
          }
        }
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [idx, paused, active.duration]);

  // Keyboard nav
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight") { e.preventDefault(); next(); }
      else if (e.key === "ArrowLeft") { e.preventDefault(); prev(); }
      else if (e.key === " ") { e.preventDefault(); setPaused((p) => !p); }
      else if (e.key === "Escape") { navigate("/login"); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [next, prev, navigate]);

  const globalProgress = useMemo(() => {
    const done = CARDS.slice(0, idx).reduce((s, c) => s + c.duration, 0);
    const cur = active.duration * progress;
    return Math.min(1, (done + cur) / TOTAL);
  }, [idx, progress, active.duration]);

  const cardEl = useMemo(() => {
    switch (active.id) {
      case "intro": return <IntroCard active />;
      case "triage": return <TriageCard active />;
      case "commitments": return <CommitmentsCard active />;
      case "reminder": return <ReminderCard active />;
      case "projects": return <ProjectsCard active />;
      case "cta": return <CtaCard onExit={() => navigate("/login")} />;
      default: return null;
    }
  }, [active.id, navigate]);

  return (
    <div className="min-h-screen bg-white text-neutral-900 relative overflow-hidden" data-testid="demo-tour-root">
      {/* Ambient background */}
      <div className="absolute inset-0 -z-0 pointer-events-none">
        <div className="absolute -top-32 -left-32 h-96 w-96 rounded-full bg-neutral-200 blur-3xl opacity-70" />
        <div className="absolute -bottom-32 -right-32 h-96 w-96 rounded-full bg-neutral-200 blur-3xl opacity-50" />
      </div>

      {/* Top bar */}
      <div className="absolute top-0 inset-x-0 z-30 px-6 py-4 flex items-center justify-between">
        <Link to="/login" className="flex items-center gap-2.5" data-testid="tour-home-link">
          <PZeroLogo height={28} variant="onLight" />
        </Link>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setPaused((p) => !p)}
            data-testid="tour-play-pause"
            className="h-9 w-9 rounded-full border border-neutral-200 bg-white flex items-center justify-center text-neutral-700 hover:bg-neutral-50"
            aria-label={paused ? "Play" : "Pause"}
          >
            {paused ? <Play size={14} /> : <Pause size={14} />}
          </button>
          <button
            onClick={() => navigate("/login")}
            data-testid="tour-skip"
            className="h-9 px-3 rounded-full border border-neutral-200 bg-white text-xs font-medium text-neutral-700 hover:bg-neutral-50"
          >
            Skip tour
          </button>
          <button
            onClick={() => navigate("/login")}
            data-testid="tour-close"
            className="h-9 w-9 rounded-full border border-neutral-200 bg-white flex items-center justify-center text-neutral-700 hover:bg-neutral-50"
            aria-label="Close"
          >
            <X size={14} />
          </button>
        </div>
      </div>

      {/* Segmented progress */}
      <div className="absolute top-16 inset-x-0 z-20 px-6" data-testid="tour-progress">
        <div className="max-w-6xl mx-auto flex items-center gap-1.5">
          {CARDS.map((c, i) => {
            const p = i < idx ? 1 : i === idx ? progress : 0;
            return (
              <div key={c.id} className="flex-1 h-1 rounded-full bg-neutral-200 overflow-hidden">
                <div
                  className="h-full bg-neutral-900 transition-[width] duration-100 ease-linear"
                  style={{ width: `${p * 100}%` }}
                />
              </div>
            );
          })}
        </div>
        <div className="max-w-6xl mx-auto mt-2 flex items-center justify-between text-[10px] font-mono uppercase text-neutral-500">
          <div>{String(idx + 1).padStart(2, "0")} · {active.label}</div>
          <div>{Math.round(globalProgress * (TOTAL / 1000))}s / {Math.round(TOTAL / 1000)}s</div>
        </div>
      </div>

      {/* Card viewport */}
      <div className="relative z-10 min-h-screen pt-32 pb-24 flex items-center">
        <AnimatePresence mode="wait">
          <motion.div
            key={active.id}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
            className="w-full"
            data-testid={`tour-card-${active.id}`}
          >
            {cardEl}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Prev / Next controls */}
      <div className="absolute bottom-8 inset-x-0 z-20 px-6 flex items-center justify-between max-w-6xl mx-auto">
        <button
          onClick={prev}
          disabled={idx === 0}
          data-testid="tour-prev"
          className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-md text-neutral-600 hover:text-neutral-900 disabled:opacity-30"
        >
          <ChevronLeft size={16} /> Previous
        </button>
        <div className="text-[11px] font-mono text-neutral-500 select-none">
          <span className="hidden sm:inline">← → to nav · space to pause · esc to exit</span>
        </div>
        <button
          onClick={next}
          disabled={idx === CARDS.length - 1}
          data-testid="tour-next"
          className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-md text-neutral-900 hover:bg-neutral-100 disabled:opacity-30"
        >
          Next <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
}
